import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import countryCode from '../../../../../../utils/data/json/country-code.json';
import * as Flags from 'country-flag-icons/react/3x2';

const linkRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
interface CountryCode {
  name: string;
  web_code: string;
  country_code: string;
  flag: string;
  phone_code: string;
  phone_number_length: number;
}
type Props = {
  phoneNumber: any;
  phoneCode: any;
  orgCountryCode?: string;
  handleDetailsChange: (fieldName: any, newValue: any) => void;
  setPhoneParentError: (error: string | null) => void;
  hasPermission: boolean;
};

const CountryFlag = ({ webCode, style }: { webCode: string; style?: React.CSSProperties }) => {
  const Flag = Flags[webCode as keyof typeof Flags] as unknown as React.ComponentType<{ style?: React.CSSProperties }>;
  return Flag ? <Flag style={{ width: 20, height: 14, display: 'block', ...style }} /> : null;
};

const SettingsOrgMobile = ({ phoneNumber, phoneCode, orgCountryCode, handleDetailsChange, hasPermission, setPhoneParentError }: Props) => {
  const countryCodes = countryCode as CountryCode[];
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const findCountry = (code: string) => {
    if (!code) return null;
    return countryCodes.find(cc =>
      cc.phone_code === code || cc.phone_code === `+${code}` ||
      cc.web_code === code || cc.country_code === code
    ) || null;
  };

  const validatePhoneNumber = (phone: string, code: string) => {
    const selectedCountry = findCountry(code);
    if (!selectedCountry) return true;

    const allowedLengths = Array.isArray(selectedCountry.phone_number_length)
    ? selectedCountry.phone_number_length
    : [selectedCountry.phone_number_length];

  if (!allowedLengths.includes(phone.length)) {
    setPhoneError(`Phone number must be valid.`);
    setPhoneParentError('Phone number must be valid.')
    return false;
  }
  setPhoneError(null);
  setPhoneParentError(null)
  return true;
  };
  return (
    <Grid container spacing={2} sx={{ alignItems: 'center' }}>
      <style>
        {`.css-1h51icj-MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input {
                      padding: 5.5px 14px !important;
                      font-size: 0.75rem !important;
          }`}
      </style>
      <Grid item xs={12} md={4}>
        <Typography variant="h5" sx={{ fontSize: '14px' }}>
          Phone number
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <div className="row mt-1 mb-2">
          <div className="col-5 ">
            <Autocomplete
              disabled={!hasPermission}
              options={countryCodes}
              getOptionLabel={option => `${option.phone_code}`}
              componentsProps={{ paper: { sx: { minWidth: 250 } } }}
              filterOptions={(options, { inputValue }) => {
                return options.filter(option => option.phone_code.toLowerCase().includes(inputValue.toLowerCase()));
              }}
              value={findCountry(orgCountryCode || phoneCode)}
                onChange={(e, option) => {
                handleDetailsChange('phoneCode', option?.phone_code || '');
                handleDetailsChange('countryCode', option?.web_code || '');
                if (!option) {
                  setPhoneError(null);
                  setPhoneParentError(null);
                }
              }}
              renderInput={params => {
                const selectedCountry = findCountry(orgCountryCode || phoneCode);
                return (
                  <TextField
                    name="countryCode"
                    {...params}
                    label=""
                    size="small"
                    placeholder="Country code"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedCountry ? (
                        <InputAdornment position="start">
                          <CountryFlag webCode={selectedCountry.web_code} />
                        </InputAdornment>
                      ) : params.InputProps.startAdornment,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: isDarkMode ? theme.placeholder.dark : theme.placeholder.light,
                        opacity: 1,
                      },
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                return (
                  <Box component="li" key={option.web_code} sx={{ gap: 1, display: 'flex', alignItems: 'center' }} {...optionProps}>
                    <CountryFlag webCode={option.web_code} style={{ marginRight: 6, flexShrink: 0 }} />
                    {option.phone_code} {option.name}
                  </Box>
                );
              }}
            />
          </div>
          <div className="col-7">
            <TextField
              disabled={!hasPermission}
              sx={{
                '& .MuiOutlinedInput-root': { height: '40px' },
                '& .MuiInputBase-input::placeholder': {
                  color: isDarkMode ? theme.placeholder.dark : theme.placeholder.light,
                  opacity: 1,
                },
              }}
              className="w-100"
              name="phoneNumber"
              value={phoneNumber}
              onChange={e => {
                let numericValue = e.target.value.replace(/\D/g, '');
                if (numericValue.startsWith('-')) {
                  numericValue = numericValue.slice(1);
                }

                handleDetailsChange('phoneNumber', numericValue);
                validatePhoneNumber(numericValue, phoneCode);
              }}
              error={!!phoneError}
              helperText={phoneError}
              size="small"
              placeholder="Phone number"
              inputProps={{
                style: {
                  fontSize: 12,
                },
              }}
              type="text"
            />
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default SettingsOrgMobile;
